import type { NamedExoticComponent } from "react";
import { createElement, memo, useMemo } from "react";

import type {
  IHeadingTypographyProps,
  ISpanTypographyProps,
  ITypographyProps,
} from "./types";
import { generateTypographyClassName } from "./helper";

import type { IFontType } from "@/interfaces/styles";

const Typography = memo(function Typography(props: ITypographyProps) {
  const {
    children,
    className,
    fontType,
    themeColor,
    fontSize,
    fontWeight,
    disableSelect,
    ellipsis,
    textWrap,
    textCenter,
    ...htmlAttribute
  } = props;

  const classNames = useMemo(
    () =>
      generateTypographyClassName(
        {
          ...htmlAttribute,
          fontType,
          themeColor,
          fontSize,
          fontWeight,
          disableSelect,
          ellipsis,
          textWrap,
          textCenter,
        },
        className,
      ),
    [
      htmlAttribute,
      fontType,
      themeColor,
      fontSize,
      fontWeight,
      disableSelect,
      ellipsis,
      textWrap,
      textCenter,
      className,
    ],
  );

  return (
    <p className={classNames} {...htmlAttribute}>
      {children}
    </p>
  );
}) as NamedExoticComponent<ITypographyProps> & {
  Heading: NamedExoticComponent<IHeadingTypographyProps>;
  Span: NamedExoticComponent<ISpanTypographyProps>;
};

const Heading = memo(function Heading(props: IHeadingTypographyProps) {
  const {
    children,
    className,
    headingType,
    fontType: propsFontType,
    ...htmlAttribute
  } = props;

  const fontType: IFontType = propsFontType || `title-${headingType}`;

  return useMemo(
    () =>
      createElement(
        headingType,
        {
          ...htmlAttribute,
          className: generateTypographyClassName(
            { ...htmlAttribute, fontType },
            className,
          ),
        },
        children,
      ),
    [headingType, htmlAttribute, fontType, className, children],
  );
});

const Span = memo(function Span(props: ISpanTypographyProps) {
  const { children, fontType, className, themeColor, ...htmlAttribute } = props;

  const classNames = useMemo(
    () =>
      generateTypographyClassName(
        { ...htmlAttribute, fontType, themeColor },
        className,
      ),
    [htmlAttribute, fontType, themeColor, className],
  );

  return (
    <span className={classNames} {...htmlAttribute}>
      {children}
    </span>
  );
});

Typography.Span = Span;
Typography.Heading = Heading;
export { Typography };
